import { useState, useEffect } from 'react';
import { supabasePortal as supabase } from '../lib/supabase';
import { i18n, LANG_LABELS, detectLang } from './portalI18n';

const CARD_STYLE = {
  background: 'white',
  borderRadius: 20,
  padding: '48px 40px',
  maxWidth: 440,
  width: '90%',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};

export default function PortalRegisterPage({ token, onSuccess }) {
  const [lang, setLang] = useState(detectLang);
  const t = i18n[lang];

  const [invite, setInvite] = useState(null);   // { clientName, clientEmail }
  const [tokenError, setTokenError] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(true);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Fetch invite details from token
  useEffect(() => {
    if (!token) { setTokenError(true); setTokenLoading(false); return; }
    fetch(`/api/portal/invite/${token}`)
      .then(r => r.json())
      .then(data => {
        if (!data.valid) { setTokenError(true); }
        else {
          setInvite(data);
          if (data.clientEmail) setEmail(data.clientEmail);
        }
      })
      .catch(() => setTokenError(true))
      .finally(() => setTokenLoading(false));
  }, [token]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError(t.passwordMismatch); return; }
    if (password.length < 6) { setError(t.passwordTooShort); return; }
    setBusy(true);
    try {
      const res = await fetch(`/api/portal/register/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'email_exists') { setError(t.emailAlreadyUsed); }
        else { setError(data.error || t.registerError); }
        return;
      }
      // Registration succeeded — now sign in to get a real session
      const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
      if (signInErr) { setError(t.loginError); return; }
      onSuccess(signInData.session);
    } catch {
      setError(t.registerError);
    } finally {
      setBusy(false);
    }
  };

  if (tokenLoading) {
    return (
      <div style={pageStyle}>
        <div style={{ ...CARD_STYLE, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>
          <p style={{ color: '#64748b' }}>{t.loading}</p>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div style={pageStyle}>
        <div style={{ ...CARD_STYLE, textAlign: 'center', direction: t.dir }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔗</div>
          <h2 style={{ fontSize: 20, color: '#dc2626', margin: '0 0 8px' }}>{t.linkExpired}</h2>
          <p style={{ color: '#64748b', fontSize: 14 }}>{t.tokenExpired}</p>
          <button
            onClick={() => window.location.replace('/portal')}
            style={{ marginTop: 20, padding: '10px 24px', borderRadius: 10, border: 'none', background: '#4338ca', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
          >
            {t.backToLogin}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...pageStyle, direction: t.dir }}>
      <div style={CARD_STYLE}>
        {/* Language selector */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 24 }}>
          {Object.entries(LANG_LABELS).map(([code, label]) => (
            <button key={code}
              onClick={() => { setLang(code); localStorage.setItem('portal_lang', code); }}
              style={{
                padding: '4px 10px', borderRadius: 20, border: '1px solid',
                borderColor: lang === code ? '#4338ca' : '#e2e8f0',
                background: lang === code ? '#4338ca' : 'white',
                color: lang === code ? 'white' : '#64748b',
                cursor: 'pointer', fontSize: 12, fontWeight: 600,
              }}
            >{label}</button>
          ))}
        </div>

        <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 12 }}>🏦</div>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', margin: '0 0 6px', textAlign: 'center' }}>
          {t.registerTitle}
        </h1>
        <p style={{ fontSize: 13, color: '#4338ca', margin: '0 0 28px', textAlign: 'center', fontWeight: 600 }}>
          {t.registerSubtitle(invite?.clientName || '')}
        </p>

        <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label={t.chooseEmail} dir={lang === 'en' ? 'left' : 'right'}>
            <input
              type="email" required value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t.emailPlaceholder}
              style={inputStyle} dir="ltr"
            />
          </Field>
          <Field label={t.choosePassword} dir={lang === 'en' ? 'left' : 'right'}>
            <input
              type="password" required value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={t.passwordPlaceholder}
              style={inputStyle} dir="ltr"
            />
          </Field>
          <Field label={t.confirmPassword} dir={lang === 'en' ? 'left' : 'right'}>
            <input
              type="password" required value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder={t.passwordPlaceholder}
              style={inputStyle} dir="ltr"
            />
          </Field>

          {error && (
            <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
              {error}
            </div>
          )}

          <button
            type="submit" disabled={busy}
            style={{
              padding: '13px', borderRadius: 10, border: 'none',
              background: busy ? '#a5b4fc' : '#4338ca',
              color: 'white', fontSize: 15, fontWeight: 700,
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            {busy ? t.registering : t.registerBtn}
          </button>

          <p style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', margin: 0 }}>
            {t.alreadyRegistered}
            <button
              type="button"
              onClick={() => window.location.replace('/portal')}
              style={{ background: 'none', border: 'none', color: '#4338ca', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            >{t.loginLink}</button>
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, dir, children }) {
  return (
    <div style={{ textAlign: dir }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'linear-gradient(135deg, #1e3a8a 0%, #4338ca 100%)',
  fontFamily: "'Segoe UI', -apple-system, sans-serif",
};

const inputStyle = {
  width: '100%', padding: '10px 14px',
  border: '1.5px solid #e2e8f0', borderRadius: 10,
  fontSize: 14, outline: 'none', boxSizing: 'border-box',
};
